import React, { createContext, useContext, useState, useEffect } from "react";
import {
  TIMEZONE_OPTIONS,
  getTimezone,
  setTimezone as setTimezoneStorage,
} from "../libs/utils";

const TimezoneContext = createContext();

export const useTimezone = () => {
  const context = useContext(TimezoneContext);
  if (!context) {
    throw new Error("useTimezone must be used within a TimezoneProvider");
  }
  return context;
};

export const TimezoneProvider = ({ children }) => {
  const [currentTimezone, setCurrentTimezone] = useState(() => {
    const stored = localStorage.getItem("cms_selected_timezone") || "UTC";
    return stored;
  });

  const setTimezone = (timezoneKey) => {
    if (TIMEZONE_OPTIONS[timezoneKey]) {
      setTimezoneStorage(timezoneKey);
      setCurrentTimezone(timezoneKey);
      // Dispatch event for any non-React code that might be listening
      window.dispatchEvent(new Event("timezoneChanged"));
    }
  };

  // Listen to storage changes (for cross-tab synchronization)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "cms_selected_timezone") {
        setCurrentTimezone(e.newValue || "UTC");
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Listen to custom timezone change event
  useEffect(() => {
    const handleTimezoneChange = () => {
      const stored = localStorage.getItem("cms_selected_timezone") || "UTC";
      setCurrentTimezone(stored);
    };

    window.addEventListener("timezoneChanged", handleTimezoneChange);
    return () =>
      window.removeEventListener("timezoneChanged", handleTimezoneChange);
  }, []);

  // Get current timezone value
  const timezoneValue =
    TIMEZONE_OPTIONS[currentTimezone]?.value || TIMEZONE_OPTIONS.UTC.value;

  const value = {
    timezone: currentTimezone,
    timezoneValue,
    setTimezone,
    TIMEZONE_OPTIONS,
  };

  return (
    <TimezoneContext.Provider value={value}>
      {children}
    </TimezoneContext.Provider>
  );
};
